import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  RowActions,
  type RowActionItem,
} from '@/components/ui';
import type { DepositWithdrawal } from '../types';

export interface WithdrawalActionsProps {
  withdrawal: DepositWithdrawal;
  canWrite: boolean;
  busy?: boolean;
  onApprove: (requestId: string) => void;
  onPay: (requestId: string) => void;
  onReject: (input: { requestId: string; reason: string }) => void;
}

/**
 * 출금 신청 1건의 액션 (deduct-on-approve 흐름):
 * REQUESTED → 승인/반려, APPROVED → 지급/반려. 반려는 사유 입력 모달.
 */
export const WithdrawalActions = ({
  withdrawal,
  canWrite,
  busy,
  onApprove,
  onPay,
  onReject,
}: WithdrawalActionsProps) => {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!canWrite) {
    return (
      <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
        -
      </span>
    );
  }

  const actions: RowActionItem[] = [];
  if (withdrawal.status === 'REQUESTED') {
    actions.push({ key: 'approve', label: '승인', onClick: () => onApprove(withdrawal.id) });
  }
  if (withdrawal.status === 'APPROVED') {
    actions.push({ key: 'pay', label: '지급 완료', onClick: () => onPay(withdrawal.id) });
  }
  if (withdrawal.status === 'REQUESTED' || withdrawal.status === 'APPROVED') {
    actions.push({
      key: 'reject',
      label: '반려',
      variant: 'danger',
      separatorBefore: true,
      onClick: () => {
        setReason('');
        setError(null);
        setRejectOpen(true);
      },
    });
  }

  const handleReject = () => {
    if (reason.trim() === '') {
      setError('반려 사유를 입력하세요.');
      return;
    }
    onReject({ requestId: withdrawal.id, reason: reason.trim() });
    setRejectOpen(false);
  };

  return (
    <>
      {actions.length > 0 ? (
        <RowActions triggerLabel="출금 처리" actions={actions} />
      ) : (
        <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
          -
        </span>
      )}

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>출금 신청 반려</DialogTitle>
            <DialogDescription>반려 사유를 입력하세요. 신청 행 메모에 기록됩니다.</DialogDescription>
          </DialogHeader>
          {error ? (
            <div
              className="rounded-md p-2.5 text-[12px]"
              role="alert"
              style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
            >
              {error}
            </div>
          ) : null}
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={200}
            aria-label="반려 사유"
            placeholder="반려 사유"
          />
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">
                취소
              </Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={busy}
              onClick={handleReject}
              data-testid="withdrawal-reject-submit"
            >
              반려
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
